import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Typography, 
  Container,
  Card,
  CardContent,
  alpha,
  useTheme,
} from '@mui/material';

const PrivacyPolicyPage = () => {
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>Privacy Policy - RatePly</title>
        <meta name="description" content="RatePly Privacy Policy - How we collect, use, and protect your personal information." />
      </Helmet>
      
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              mb: 2,
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: alpha(theme.palette.common.white, 0.9),
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
        </Box>

        {/* Content */}
        <Card
          elevation={4}
          sx={{
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Privacy Policy for RatePly
            </Typography>

            <Box sx={{ '& > *': { mb: 3 } }}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  1. Information We Collect
                </Typography>
                <Typography variant="body1" paragraph>
                  RatePly is committed to protecting your privacy. We collect minimal information to provide our currency conversion services:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Usage data: Currency pairs you convert, conversion amounts (anonymized)</li>
                  <li>Technical data: Browser type, device information, IP address (for security)</li>
                  <li>Optional: Email address if you contact us for support</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  2. How We Use Your Information
                </Typography>
                <Typography variant="body1" paragraph>
                  We use the collected information solely to:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Provide accurate currency conversion services</li>
                  <li>Improve our service performance and user experience</li>
                  <li>Respond to support inquiries</li>
                  <li>Ensure security and prevent abuse</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  3. Data Storage and Security
                </Typography>
                <Typography variant="body1" paragraph>
                  Your data security is important to us:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>We use industry-standard encryption and security measures</li>
                  <li>Currency conversion data is cached temporarily for performance</li>
                  <li>We do not store personal financial information</li>
                  <li>Data is processed in secure, compliant data centers</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  4. Third-Party Services
                </Typography>
                <Typography variant="body1" paragraph>
                  We use the following third-party services:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>ExchangeRate-API for real-time currency data</li>
                  <li>Web analytics tools to understand usage patterns (anonymized)</li>
                  <li>Cloud hosting services for reliable service delivery</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  5. Cookies and Local Storage
                </Typography>
                <Typography variant="body1" paragraph>
                  We use minimal cookies and local storage for:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Remembering your preferred currencies</li>
                  <li>Essential website functionality</li>
                  <li>Analytics (with your consent)</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  6. Your Rights
                </Typography>
                <Typography variant="body1" paragraph>
                  You have the right to:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Access any personal data we hold about you</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Opt-out of non-essential cookies</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  7. Contact Us
                </Typography>
                <Typography variant="body1" paragraph>
                  If you have any questions about this Privacy Policy or your data, please contact us at:
                </Typography>
                <Typography variant="body2">
                  Email: privacy@rateply.com
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  8. Changes to This Policy
                </Typography>
                <Typography variant="body1">
                  We may update this Privacy Policy from time to time. We will notify users of any significant changes by posting the updated policy on this page with a new "Last updated" date.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default PrivacyPolicyPage;
